import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Package } from '@revenuecat/purchases-js'
import { Button } from 'components'
import { motion } from 'framer-motion'
import { useAnalytics } from 'hooks'
import { ArrowLeft, Bell, Lock, Star } from 'lucide-react'
import { selectBusinessId } from 'modules/authentication'
import { purchasePackage, selectRevenueCat, selectShowUpgrade } from 'modules/billing'
import { fetchBusinessBilling } from 'modules/billing/slices/credit-balance.slice'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { BillingService } from 'openapi'
import React, { useEffect, useRef, useState } from 'react'

/**
 * Check if the current user is a tester and grant them the selected plan.
 * Returns true if a subscription was granted.
 */
async function checkTesterGrant(productVendorId: string): Promise<boolean> {
  try {
    const result = await BillingService.billingControllerCheckTesterGrantV1({ productVendorId })
    return result.granted === true
  } catch {
    return false
  }
}

// Constants
const TRIAL_DURATION_DAYS = 7
const TRIAL_REMINDER_DAY = 5
const DEFAULT_MONTHLY_PRICE = '$19.90'

interface TrialCheckoutProps {
  onNext: () => void
  onBack: () => void
  isSubmitting: boolean
  onClose?: () => void
  source?: string
  selectedPackage?: Package
  onPaymentSuccess?: () => Promise<void>
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
}

// Helper function to extract package properties for analytics
const getPackageAnalyticsProps = (pkg: Package) => {
  const planName = pkg.webBillingProduct.title
  const billingPeriod = pkg.webBillingProduct.period?.unit
  const price = pkg.webBillingProduct?.price?.amountMicros
    ? pkg.webBillingProduct.price.amountMicros / 1000000
    : undefined
  const currency = pkg.webBillingProduct?.price?.currency
  const includesTrial = !!pkg.webBillingProduct?.freeTrialPhase

  return {
    plan_name: planName,
    billing_period: billingPeriod,
    price,
    currency,
    includes_trial: includesTrial,
  }
}

export const TrialCheckout: React.FC<TrialCheckoutProps> = ({
  onNext,
  onBack,
  isSubmitting,
  onClose,
  source = 'onboarding',
  selectedPackage,
  onPaymentSuccess,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { track } = useAnalytics()
  const dispatch = useAppDispatch()
  const hasSkipped = useRef(false)

  const { currentOffering, isConfigured } = useAppSelector(selectRevenueCat)
  const showUpgrade = useAppSelector(selectShowUpgrade)
  const businessId = useAppSelector(selectBusinessId)

  const resolvedPackage = selectedPackage ?? currentOffering?.availablePackages?.[0] ?? null
  const billingProduct = resolvedPackage?.webBillingProduct
  const displayPrice = billingProduct?.price?.formattedPrice || DEFAULT_MONTHLY_PRICE
  const isYearly = billingProduct?.period?.unit === 'year'
  // RC strips the trial phase per customer (any past trial start counts as a
  // purchase) — a stripped customer is charged in full IMMEDIATELY, so every
  // trial claim below must key off this. Null package = offering unavailable,
  // keep the historical trial framing.
  const includesTrial = resolvedPackage ? !!billingProduct?.freeTrialPhase : true

  // Skip timeline if user is already subscribed
  useEffect(() => {
    if (isConfigured && !showUpgrade && !hasSkipped.current) {
      hasSkipped.current = true
      onNext()
    }
  }, [isConfigured, showUpgrade, onNext])

  // Track trial checkout viewed (trial-stripped customers see the plain
  // checkout, not the timeline — keep them out of the trial funnel metric)
  useEffect(() => {
    if (isConfigured && showUpgrade && includesTrial) {
      track(AnalyticsEvents.Paywall.TRIAL_TIMELINE_VIEWED, {
        source,
      })
    }
  }, [isConfigured, showUpgrade, track, source, includesTrial])

  // Calculate charge date (today + trial duration)
  const chargeDate = new Date()
  chargeDate.setDate(chargeDate.getDate() + TRIAL_DURATION_DAYS)
  const formattedChargeDate = chargeDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })

  // Complete checkout - save brand kit + trigger suggestions, then proceed
  const completeCheckout = async () => {
    try {
      await onPaymentSuccess?.()
    } catch (err) {
      console.warn('[TrialCheckout] onPaymentSuccess failed (non-blocking):', err)
    }
    setIsProcessing(false)
    if (onClose) {
      onClose()
    } else {
      onNext()
    }
  }

  const handleStartTrial = async () => {
    // Track CTA clicked (without package props for early exits)
    track(AnalyticsEvents.Paywall.TRIAL_CHECKOUT_CTA_CLICKED, {
      source,
    })

    setIsProcessing(true)
    setErrorMessage(null)

    // Check if user is a tester and grant subscription
    try {
      const vendorId = resolvedPackage?.webBillingProduct?.identifier
      if (!vendorId) throw new Error('no vendor id')
      const granted = await checkTesterGrant(vendorId)
      if (granted) {
        if (businessId) {
          dispatch(fetchBusinessBilling(businessId))
        }
        track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
          source,
          reason: 'tester_grant',
        })
        track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
          source,
          skipped: true,
          reason: 'tester_grant',
        })
        await completeCheckout()
        return
      }
    } catch (err) {
      console.warn('[TrialCheckout] Tester grant check failed, proceeding with payment:', err)
    }

    // Regular payment flow
    if (!isConfigured) {
      setErrorMessage('RevenueCat not configured')
      setIsProcessing(false)
      return
    }

    const packageToPurchase = resolvedPackage
    if (!packageToPurchase) {
      setErrorMessage('No packages available')
      setIsProcessing(false)
      return
    }

    const packageProps = getPackageAnalyticsProps(packageToPurchase)

    try {
      // Track checkout started
      track(AnalyticsEvents.Paywall.CHECKOUT_STARTED, {
        ...packageProps,
        source,
      })

      // The thunk rejects via rejectWithValue, so the real message lives in
      // result.payload — result.error.message is always the generic
      // "Rejected" and '__cancelled__' would never match against it.
      const result = await dispatch(purchasePackage({ packageToPurchase }))

      if (result.meta.requestStatus === 'rejected') {
        if (result.payload === '__cancelled__') {
          setIsProcessing(false)
          return
        }
        throw new Error((result.payload as string) || 'Purchase failed')
      }

      if (businessId) {
        dispatch(fetchBusinessBilling(businessId))
      }

      // Track checkout completed
      track(AnalyticsEvents.Paywall.CHECKOUT_COMPLETED, {
        ...packageProps,
        source,
      })

      await completeCheckout()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Purchase failed'
      setErrorMessage(message)
      console.error('Purchase failed:', error)

      // Track checkout failed
      track(AnalyticsEvents.Paywall.CHECKOUT_FAILED, {
        ...packageProps,
        source,
        error_message: message,
      })
      setIsProcessing(false)
    }
  }

  // Show loading while checking subscription
  if (!isConfigured) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const isOnboarding = source === 'onboarding'

  return (
    <motion.div
      className="relative flex h-full w-full flex-col px-6 py-8"
      variants={isOnboarding ? containerVariants : undefined}
      initial={isOnboarding ? 'hidden' : false}
      animate={isOnboarding ? 'visible' : false}
    >
      {/* Back button */}
      <motion.button
        variants={isOnboarding ? itemVariants : undefined}
        onClick={onBack}
        className="group absolute top-4 left-4 rounded-lg p-2 transition-colors hover:bg-gray-100"
        aria-label="Go back"
      >
        <ArrowLeft size={20} className="text-black transition-colors group-hover:text-gray-900" />
      </motion.button>

      {/* Title */}
      <motion.div
        variants={isOnboarding ? itemVariants : undefined}
        className="mt-4 mb-8 text-center"
      >
        <h2 className="text-2xl font-bold text-gray-900">
          {includesTrial ? 'How your free trial works?' : 'Confirm your plan'}
        </h2>
      </motion.div>

      {/* Timeline with doubled side padding — only when this customer actually
          gets the trial; RC strips the phase for past trial starters and they
          are charged immediately. */}
      {includesTrial ? (
        <div className="mx-auto mb-10 w-full max-w-md flex-1 px-4 sm:px-6">
          <div className="relative pl-9">
            {/* Continuous thick gradient bar */}
            <div className="absolute top-[-12px] bottom-[-16px] left-0 w-5 rounded-full bg-[linear-gradient(to_bottom,var(--acid)_0%,var(--acid)_50%,var(--acid)_85%,transparent_100%)] shadow-[var(--shadow-acid-cta)]" />

            {/* Timeline items */}
            <div className="flex flex-col gap-5">
              {/* Today */}
              <motion.div variants={isOnboarding ? itemVariants : undefined} className="relative">
                {/* Icon positioned on the bar */}
                <div className="absolute top-0 -left-9 flex h-5 w-5 items-center justify-center">
                  <Lock className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Today</div>
                  <div className="text-sm text-black">
                    Full access to all creative types with your monthly credits.
                  </div>
                </div>
              </motion.div>

              {/* Day 5 */}
              <motion.div variants={isOnboarding ? itemVariants : undefined} className="relative">
                {/* Icon positioned on the bar */}
                <div className="absolute top-0 -left-9 flex h-5 w-5 items-center justify-center">
                  <Bell className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Day {TRIAL_REMINDER_DAY}</div>
                  <div className="text-sm text-black">
                    We'll remind you with an email or notification that your trial is ending
                  </div>
                </div>
              </motion.div>

              {/* Day 7 */}
              <motion.div variants={isOnboarding ? itemVariants : undefined} className="relative">
                {/* Icon positioned on the bar */}
                <div className="absolute top-0 -left-9 flex h-5 w-5 items-center justify-center">
                  <Star className="h-4 w-4 text-white/70" />
                </div>
                <div>
                  <div className="font-bold text-gray-900">Day {TRIAL_DURATION_DAYS}</div>
                  <div className="text-sm text-black">
                    You'll be charged on {formattedChargeDate}, cancel anytime before.
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto mb-10 flex w-full max-w-md flex-1 items-center justify-center px-4 sm:px-6">
          <div className="text-center">
            <div className="font-bold text-gray-900">Full access from today</div>
            <div className="text-sm text-black">
              All creative types with your monthly credits, on your team's plan.
            </div>
          </div>
        </div>
      )}

      {/* Pricing */}
      <motion.div variants={isOnboarding ? itemVariants : undefined} className="mb-6 text-center">
        <p className="text-black">
          {includesTrial ? `${TRIAL_DURATION_DAYS}-day free trial, then` : 'Billed today:'}
          <br />
          <span className="font-bold text-gray-900">
            {displayPrice} {isYearly ? 'per year' : 'per month'},
          </span>{' '}
          cancel anytime
        </p>
      </motion.div>

      {/* Error Message */}
      {errorMessage && (
        <motion.div
          variants={isOnboarding ? itemVariants : undefined}
          className="mx-auto mb-4 max-w-md"
        >
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        </motion.div>
      )}

      {/* Start Trial button */}
      <motion.div variants={isOnboarding ? itemVariants : undefined} className="mb-6">
        {/* Mobile button */}
        <div className="md:hidden">
          <Button
            onClick={handleStartTrial}
            variant="t3-primary"
            size="t3-lg"
            disabled={isProcessing || isSubmitting}
            className="w-full"
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                Processing...
              </span>
            ) : includesTrial ? (
              `Start my ${TRIAL_DURATION_DAYS}-day free trial`
            ) : (
              'Subscribe now'
            )}
          </Button>
        </div>

        {/* Desktop button */}
        <div className="hidden md:block">
          <Button
            onClick={handleStartTrial}
            variant="t3-primary"
            size="t3-lg"
            disabled={isProcessing || isSubmitting}
            className="mx-auto block w-auto px-8"
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                Processing...
              </span>
            ) : includesTrial ? (
              `Start my ${TRIAL_DURATION_DAYS}-day free trial`
            ) : (
              'Subscribe now'
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}
